import { useCallback, useMemo } from "react"

import { SearchDetailResult } from "interface/search"
import { SisetuResult, useListSisetu } from "interface/sisetu"
import { KojinResult, useListKojin } from "interface/kojin"

import { Grid, useGrid } from "components/grid"

import { SisetuPreviewDialog } from "features/sisetu/dialog/preview"
import { useSisetuGridColumns, SisetuWithKolList } from "./columns"
import { useSisetuPreviewDialog } from "features/sisetu/dialog/preview/hooks"

type SisetuPageProps = {
	search: SearchDetailResult
} & ReturnType<typeof useGrid>
export const SisetuPage = ({
	search,
	...others
}: SisetuPageProps) => {
	const {
		sisetuList: selectedSisetuList,
		sisetuCode: selectedSisetuCode,
		setSisetuList: setSelectedSisetuList,
		openSisetu: openSisetuPreviewDialog,
		close: closeSisetuPreviewDialog
	} = useSisetuPreviewDialog()

	const { apiRef } = others

	const onFetchedSisetuList = useCallback(
		(result: SisetuResult[]) => {
			result.sort((a, b) => (
				(a.score?.rank && b.score?.rank) ? a.score.rank - b.score.rank :
				(a.sisetuNameKana && b.sisetuNameKana) ? a.sisetuNameKana.localeCompare(b.sisetuNameKana) :
				0
			))
		},
		[]
	)
	const onFetchedKojinList = useCallback(
		(result: KojinResult[]) => {
			result.sort((a, b) => (
				(a.score?.rank && b.score?.rank) ? a.score.rank - b.score.rank :
				(a.isiNameKana && b.isiNameKana) ? a.isiNameKana.localeCompare(b.isiNameKana) :
				0
			))
		},
		[]
	)

	const sisetuListState = useListSisetu(search.searchId, undefined, {
		updateTime: search.updateTime,
		rankLimit: others.options.sisetuLimit,
		onFetched: onFetchedSisetuList
	})
	const kojinListState = useListKojin(search.searchId, undefined, {
		updateTime: search.updateTime,
		rankLimit: others.options.kojinLimit,
		onFetched: onFetchedKojinList
	})

	const progress = useMemo(
		() => {
			if (sisetuListState.loading || kojinListState.loading) {
				const sisetuProgress = sisetuListState.loading ? (
					(sisetuListState.loaded && sisetuListState.total) ? (
						(sisetuListState.loaded / sisetuListState.total) * 100
					) : 0
				) : 100
				const kojinProgress = kojinListState.loading ? (
					(kojinListState.loaded && kojinListState.total) ? (
						(kojinListState.loaded / kojinListState.total) * 100
					) : 0
				) : 100
				//console.log(`${kojinProgress} + ${sisetuProgress}`)
				return (sisetuProgress + kojinProgress) / 2
			}
			return undefined
		},
		[sisetuListState, kojinListState]
	)

	const sisetuList = useMemo<SisetuWithKolList[]>(
		() => {
			if (!sisetuListState.data || !kojinListState.data) return []
			const sisetuList = sisetuListState.data
			const kojinList = kojinListState.data

			const kolMap: { [index: string]: SisetuWithKolList["kolList"] } = {}
			for(const kojin of kojinList) {
				if (!kojin.organ) continue
				for(const organ of kojin.organ) {
					const kolList = kolMap[organ.sisetuCode]
					if (kolList) {
						kolList.push({ kojin, organ })
					} else {
						kolMap[organ.sisetuCode] = [{ kojin, organ }]
					}
				}
			}
			return sisetuList.map(sisetu => ({
				...sisetu,
				kolList: kolMap[sisetu.sisetuCode] ?? []
			}))
		},
		[sisetuListState, kojinListState]
	)

	const onClickLink = useCallback(
		(sisetuCode: string | null) => {
			if (!apiRef.current) return
			const api = apiRef.current

			const rowDataList: SisetuWithKolList[] = []
			const rowCount = api.getRowsCount()
			for(let i = 0; i < rowCount; i++) {
				const index = api.getRowIdFromRowIndex(i) as number
				if (index >= 0 && sisetuList.length > index) {
					rowDataList.push(sisetuList[index])
				}
			}
			setSelectedSisetuList(rowDataList)
			openSisetuPreviewDialog(sisetuCode)
		},
		[setSelectedSisetuList, openSisetuPreviewDialog, apiRef, sisetuList]
	)
	const { columnsDef } = useSisetuGridColumns({ search, onClickLink })

	const onDblClick = useCallback(
		(sisetu: SisetuWithKolList) => onClickLink(sisetu.sisetuCode ?? null),
		[onClickLink]
	)
	return (
		<>
			<Grid
				{...others}
				saveKey="sisetu_grid"
				baseFileName={search.title} 
				sheetName="施設"
				loading={sisetuListState.loading || kojinListState.loading}
				progress={progress}
				columnsDef={columnsDef}
				dataList={sisetuList}
				onDblClick={onDblClick}
			/>
			{selectedSisetuCode ? (
				<SisetuPreviewDialog
					sisetuList={selectedSisetuList}
					sisetuCode={selectedSisetuCode}
					onClose={closeSisetuPreviewDialog}
				/>
			) : <></>}
		</>
	)
}